/**
 * Client error capture.
 *
 * Installed once, at boot, before anything else can throw. Reports go to
 * /api/errors where they are grouped by fingerprint — see services/api/src/errors.ts.
 */
import { VERSION, COMMIT } from './version'

let installed = false
/*
 * A page in a render loop can throw thousands of times a second. Reporting each
 * one turns the error endpoint into a write amplifier and the error table into
 * a single-error graveyard. The server also rate-limits; this is the half that
 * stops the requests being made at all.
 */
const seen = new Set<string>()
const MAX_DISTINCT = 20

function report(source: 'window' | 'promise' | 'react', message: string, stack?: string): void {
  const key = `${source}:${message}`
  if (seen.has(key) || seen.size >= MAX_DISTINCT) return
  seen.add(key)

  // keepalive so a report survives the navigation that an error often causes.
  void fetch('/api/errors', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({ message, stack, source, url: location.pathname, version: VERSION, commit: COMMIT }),
  }).catch(() => {
    // Reporting an error must never itself throw. There is nowhere left to send it.
  })
}

export function installErrorCapture(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    report('window', event.message || String(event.error), event.error?.stack)
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    report('promise', reason instanceof Error ? reason.message : String(reason), reason instanceof Error ? reason.stack : undefined)
  })
}

/** For an error boundary, which catches what the two listeners above cannot. */
export function reportReactError(error: Error, componentStack?: string): void {
  report('react', error.message, `${error.stack ?? ''}\n${componentStack ?? ''}`)
}
